<template>
    <Head title="Wallet Sharing | Settings" />
    <div class="px-4 md:px-8" data-page="settings">
        <!-- Page header -->
        <div class="sm:flex sm:justify-between sm:items-center mb-10">
            <!-- Left: Title -->
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                    Account Settings
                </h1>
            </div>
        </div>
        <!-- END PAGE HEADER -->

        <!-- TABLE -->
        <div
            class="lg:bg-white dark:bg-slate-800 dark:lg:bg-slate-800 lg:shadow-lg dark:lg:shadow-slate-900/50 lg:rounded-sm lg:border lg:border-gray-200 dark:lg:border-slate-700 mb-8"
        >
            <div class="flex flex-col lg:flex-row">
                <div
                    class="flex flex-nowrap p-0 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:border-none lg:shadow-none"
                >
                    <SettingsNav />
                </div>

                <div
                    class="p-5 lg:px-0 lg:py-0 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:mb-0 lg:border-r-0 lg:border-x-0 lg:border-y-0 lg:!border-l lg:!border-gray-200 dark:lg:!border-slate-700 lg:shadow-none"
                    style="grow: 1"
                >
                    <div class="flex flex-nowrap flex-col lg:px-10 lg:py-10">
                        <h3
                            class="text-lg font-semibold leading-7 text-slate-800 dark:text-slate-100"
                        >
                            Wallet Sharing
                        </h3>

                        <div class="mt-8">
                            <div class="flex items-center gap-2">
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="text-slate-800 dark:text-slate-100"
                                >
                                    <path
                                        d="M13.3333 11.25C13.3333 11.4801 13.1468 11.6667 12.9167 11.6667C12.6865 11.6667 12.5 11.4801 12.5 11.25C12.5 11.0199 12.6865 10.8333 12.9167 10.8333C13.1468 10.8333 13.3333 11.0199 13.3333 11.25Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M13.9583 11.25C13.9583 11.8253 13.492 12.2917 12.9167 12.2917C12.3414 12.2917 11.875 11.8253 11.875 11.25C11.875 10.6747 12.3414 10.2083 12.9167 10.2083C13.492 10.2083 13.9583 10.6747 13.9583 11.25ZM12.9167 11.6667C13.1468 11.6667 13.3333 11.4801 13.3333 11.25C13.3333 11.0199 13.1468 10.8333 12.9167 10.8333C12.6865 10.8333 12.5 11.0199 12.5 11.25C12.5 11.4801 12.6865 11.6667 12.9167 11.6667Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M15.6607 19.3393C15.9862 19.6647 16.5138 19.6647 16.8393 19.3393L18.9226 17.2559C19.248 16.9305 19.248 16.4028 18.9226 16.0774L16.8393 13.9941C16.5138 13.6686 15.9862 13.6686 15.6607 13.9941C15.3353 14.3195 15.3353 14.8472 15.6607 15.1726L16.3215 15.8333H13.3333C12.8731 15.8333 12.5 16.2064 12.5 16.6667C12.5 17.1269 12.8731 17.5 13.3333 17.5H16.3215L15.6607 18.1607C15.3353 18.4862 15.3353 19.0138 15.6607 19.3393Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M2.5 5.41667C2.5 4.44772 3.80584 2.5 5.41667 2.5H12.1569C13.2668 2.5 14.1667 3.39982 14.1667 4.5098V6.66667H15C16.3807 6.66667 17.5 7.78595 17.5 9.16667V11.25C17.5 11.7102 17.1269 12.0833 16.6667 12.0833C16.2064 12.0833 15.8333 11.7102 15.8333 11.25V9.16667C15.8333 8.70643 15.4602 8.33333 15 8.33333H5.41667C4.96933 8.33333 4.54552 8.23263 4.16667 8.05265V14.1667C4.16667 15.0871 4.91286 15.8333 5.83333 15.8333H10C10.4602 15.8333 10.8333 16.2064 10.8333 16.6667C10.8333 17.1269 10.4602 17.5 10 17.5H5.83333C3.99238 17.5 2.5 16.0076 2.5 14.1667V5.41667ZM4.16667 5.41667C4.16667 6.10702 4.72631 6.66667 5.41667 6.66667H12.5V4.5098C12.5 4.32029 12.3464 4.16667 12.1569 4.16667H5.41667C4.72631 4.16667 4.16667 4.72631 4.16667 5.41667Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <h4
                                    class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100"
                                >
                                    Your wallet shared with other users
                                </h4>
                            </div>
                            <p
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mt-1"
                            >
                                Users with access to your wallet. Your wallet
                                will show up in their account, but you won't see
                                theirs.
                            </p>
                        </div>

                        <div class="mt-3">
                            <div
                                v-if="userInvitations.length === 0"
                                class="flex flex-col justify-center items-center gap-2 py-6 pb-8 self-stretch"
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="text-slate-500 dark:text-slate-400"
                                >
                                    <g clip-path="url(#clip0_2331_4952)">
                                        <g clip-path="url(#clip1_2331_4952)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M15 5C15 4.44772 15.4477 4 16 4C18.4853 4 20.5 6.01472 20.5 8.5C20.5 10.9853 18.4853 13 16 13C15.4477 13 15 12.5523 15 12C15 11.4477 15.4477 11 16 11C17.3807 11 18.5 9.88071 18.5 8.5C18.5 7.11929 17.3807 6 16 6C15.4477 6 15 5.55228 15 5Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M9 6C7.61929 6 6.5 7.11929 6.5 8.5C6.5 9.88071 7.61929 11 9 11C10.3807 11 11.5 9.88071 11.5 8.5C11.5 7.11929 10.3807 6 9 6ZM4.5 8.5C4.5 6.01472 6.51472 4 9 4C11.4853 4 13.5 6.01472 13.5 8.5C13.5 10.9853 11.4853 13 9 13C6.51472 13 4.5 10.9853 4.5 8.5Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M6 16C4.34315 16 3 17.3431 3 19V20C3 20.5523 2.55228 21 2 21C1.44772 21 1 20.5523 1 20V19C1 16.2386 3.23858 14 6 14H12C14.7614 14 17 16.2386 17 19V20C17 20.5523 16.5523 21 16 21C15.4477 21 15 20.5523 15 20V19C15 17.3431 13.6569 16 12 16H6Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M17.9023 15C17.9023 14.4477 18.3501 14 18.9023 14H19.0016C21.763 14 24.0016 16.2386 24.0016 19V20C24.0016 20.5523 23.5538 21 23.0016 21C22.4493 21 22.0016 20.5523 22.0016 20V19C22.0016 17.3431 20.6584 16 19.0016 16H18.9023C18.3501 16 17.9023 15.5523 17.9023 15Z"
                                                fill="currentColor"
                                            />
                                        </g>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2331_4952">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                        <clipPath id="clip1_2331_4952">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <p
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                >
                                    You haven't shared your wallet yet
                                </p>
                            </div>

                            <div v-else class="overflow-hidden">
                                <div
                                    v-for="(
                                        userInvite, index
                                    ) in userInvitations"
                                    :key="userInvite.id"
                                    class="flex py-3 px-0 items-center gap-6 self-stretch"
                                    :class="{
                                        'border-b border-slate-200 dark:border-slate-700':
                                            index < userInvitations.length - 1,
                                    }"
                                >
                                    <div class="flex items-center gap-3 flex-1">
                                        <div
                                            class="flex p-3.5 items-center rounded-full bg-slate-100 dark:bg-slate-700"
                                        >
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                class="text-slate-600 dark:text-slate-300"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M10.0026 3.33317C8.39173 3.33317 7.0859 4.63901 7.0859 6.24984C7.0859 7.86067 8.39173 9.1665 10.0026 9.1665C11.6134 9.1665 12.9192 7.86067 12.9192 6.24984C12.9192 4.63901 11.6134 3.33317 10.0026 3.33317ZM5.41923 6.24984C5.41923 3.71853 7.47126 1.6665 10.0026 1.6665C12.5339 1.6665 14.5859 3.71853 14.5859 6.24984C14.5859 8.78114 12.5339 10.8332 10.0026 10.8332C7.47126 10.8332 5.41923 8.78114 5.41923 6.24984ZM6.66923 13.3332C5.28852 13.3332 4.16923 14.4525 4.16923 15.8332V16.6665C4.16923 17.1267 3.79613 17.4998 3.3359 17.4998C2.87566 17.4998 2.50256 17.1267 2.50256 16.6665V15.8332C2.50256 13.532 4.36804 11.6665 6.66923 11.6665H13.3359C15.6371 11.6665 17.5026 13.532 17.5026 15.8332V16.6665C17.5026 17.1267 17.1295 17.4998 16.6692 17.4998C16.209 17.4998 15.8359 17.1267 15.8359 16.6665V15.8332C15.8359 14.4525 14.7166 13.3332 13.3359 13.3332H6.66923Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            class="flex flex-col items-start gap-1 flex-1"
                                        >
                                            <div
                                                class="flex items-center gap-2"
                                            >
                                                <span
                                                    class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                                >
                                                    {{
                                                        getInviterName(
                                                            userInvite
                                                        )
                                                    }}
                                                </span>
                                                <div
                                                    v-if="
                                                        userInvite.status ===
                                                        'N'
                                                    "
                                                    class="flex px-2 py-0.5 justify-center items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700"
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="text-slate-500 dark:text-slate-400"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M8.00004 2.66683C5.05452 2.66683 2.66671 5.05464 2.66671 8.00016C2.66671 10.9457 5.05452 13.3335 8.00004 13.3335C10.9456 13.3335 13.3334 10.9457 13.3334 8.00016C13.3334 5.05464 10.9456 2.66683 8.00004 2.66683ZM1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016C14.6667 11.6821 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.6821 1.33337 8.00016ZM8.00004 4.66683C8.36823 4.66683 8.66671 4.96531 8.66671 5.3335V7.72402L10.1381 9.19543C10.3985 9.45578 10.3985 9.87789 10.1381 10.1382C9.87776 10.3986 9.45565 10.3986 9.1953 10.1382L7.52864 8.47157C7.40361 8.34654 7.33337 8.17697 7.33337 8.00016V5.3335C7.33337 4.96531 7.63185 4.66683 8.00004 4.66683Z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                    <span
                                                        class="text-xs font-medium leading-4 text-slate-500 dark:text-slate-400"
                                                    >
                                                        Pending your acceptance
                                                    </span>
                                                </div>
                                            </div>
                                            <span
                                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                            >
                                                {{
                                                    getInviterEmail(userInvite)
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <template
                                            v-if="userInvite.status === 'N'"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                                @click="
                                                    acceptInvitation(
                                                        userInvite.guid
                                                    )
                                                "
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g
                                                        clip-path="url(#clip0_2331_25571)"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M17.9922 2.66084C18.3636 2.93259 18.4444 3.454 18.1727 3.82544L8.41777 17.1588C8.28388 17.3418 8.0812 17.4623 7.85648 17.4926C7.63177 17.5229 7.40441 17.4603 7.22687 17.3192L1.98177 13.1526C1.6214 12.8663 1.56133 12.3421 1.84761 11.9817C2.13388 11.6213 2.65809 11.5613 3.01846 11.8476L7.58463 15.4749L16.8276 2.84134C17.0993 2.4699 17.6207 2.38908 17.9922 2.66084Z"
                                                            fill="currentColor"
                                                        />
                                                    </g>
                                                    <defs>
                                                        <clipPath
                                                            id="clip0_2331_25571"
                                                        >
                                                            <rect
                                                                width="20"
                                                                height="20"
                                                                fill="white"
                                                            />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                                Accept
                                            </button>
                                            <button
                                                type="button"
                                                class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                                @click="
                                                    openRejectModal(
                                                        userInvite.guid,
                                                        getInviterName(
                                                            userInvite
                                                        )
                                                    )
                                                "
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        clip-rule="evenodd"
                                                        d="M3.57733 3.57757C3.90277 3.25214 4.4304 3.25214 4.75584 3.57757L9.99992 8.82165L15.244 3.57757C15.5694 3.25214 16.0971 3.25214 16.4225 3.57757C16.7479 3.90301 16.7479 4.43065 16.4225 4.75609L11.1784 10.0002L16.4225 15.2442C16.7479 15.5697 16.7479 16.0973 16.4225 16.4228C16.0971 16.7482 15.5694 16.7482 15.244 16.4228L9.99992 11.1787L4.75584 16.4228C4.4304 16.7482 3.90277 16.7482 3.57733 16.4228C3.25189 16.0973 3.25189 15.5697 3.57733 15.2442L8.82141 10.0002L3.57733 4.75609C3.25189 4.43065 3.25189 3.90301 3.57733 3.57757Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                                Reject
                                            </button>
                                        </template>
                                        <template
                                            v-else-if="
                                                userInvite.status === 'A'
                                            "
                                        >
                                            <div class="flex items-center">
                                                <button
                                                    @click="
                                                        deleteWalletInvite(
                                                            userInvite.guid
                                                        )
                                                    "
                                                class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 rounded"
                                                    title="Delete this invitation"
                                                >
                                                    <Icon
                                                        icon="trash"
                                                        :width="20"
                                                        :height="20"
                                                        svg-class="text-slate-800 dark:text-slate-100"
                                                    />
                                                </button>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Second Section: Other users wallets shared with you -->
                        <div class="mt-8">
                            <div class="mb-3">
                                <div class="flex items-center gap-3 mb-2">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="text-slate-800 dark:text-slate-100"
                                    >
                                        <g clip-path="url(#clip0_2331_17885)">
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M2.5 5.41667C2.5 3.80584 3.80584 2.5 5.41667 2.5H12.1569C13.2668 2.5 14.1667 3.39982 14.1667 4.5098V6.66667H15C16.3807 6.66667 17.5 7.78595 17.5 9.16667V11.4583C17.5 11.9186 17.1269 12.2917 16.6667 12.2917C16.2064 12.2917 15.8333 11.9186 15.8333 11.4583V9.16667C15.8333 8.70643 15.4602 8.33333 15 8.33333H5.41667C4.96933 8.33333 4.54552 8.23263 4.16667 8.05265V14.1667C4.16667 15.0871 4.91286 15.8333 5.83333 15.8333H10C10.4602 15.8333 10.8333 16.2064 10.8333 16.6667C10.8333 17.1269 10.4602 17.5 10 17.5H5.83333C3.99238 17.5 2.5 16.0076 2.5 14.1667V5.41667ZM4.16667 5.41667C4.16667 6.10702 4.72631 6.66667 5.41667 6.66667H12.5V4.5098C12.5 4.32029 12.3464 4.16667 12.1569 4.16667H5.41667C4.72631 4.16667 4.16667 4.72631 4.16667 5.41667Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                d="M13.3333 11.25C13.3333 11.4801 13.1468 11.6667 12.9167 11.6667C12.6865 11.6667 12.5 11.4801 12.5 11.25C12.5 11.0199 12.6865 10.8333 12.9167 10.8333C13.1468 10.8333 13.3333 11.0199 13.3333 11.25Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M13.9583 11.25C13.9583 11.8253 13.492 12.2917 12.9167 12.2917C12.3414 12.2917 11.875 11.8253 11.875 11.25C11.875 10.6747 12.3414 10.2083 12.9167 10.2083C13.492 10.2083 13.9583 10.6747 13.9583 11.25ZM12.9167 11.6667C13.1468 11.6667 13.3333 11.4801 13.3333 11.25C13.3333 11.0199 13.1468 10.8333 12.9167 10.8333C12.6865 10.8333 12.5 11.0199 12.5 11.25C12.5 11.4801 12.6865 11.6667 12.9167 11.6667Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M16.0059 19.3393C15.6805 19.6647 15.1528 19.6647 14.8274 19.3393L12.7441 17.2559C12.4186 16.9305 12.4186 16.4028 12.7441 16.0774L14.8274 13.9941C15.1528 13.6686 15.6805 13.6686 16.0059 13.9941C16.3314 14.3195 16.3314 14.8472 16.0059 15.1726L15.3452 15.8333H18.3333C18.7936 15.8333 19.1667 16.2064 19.1667 16.6667C19.1667 17.1269 18.7936 17.5 18.3333 17.5H15.3452L16.0059 18.1607C16.3314 18.4862 16.3314 19.0138 16.0059 19.3393Z"
                                                fill="currentColor"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2331_17885">
                                                <rect
                                                    width="20"
                                                    height="20"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <h4
                                        class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100"
                                    >
                                        Other users wallets shared with you
                                    </h4>
                                </div>
                                <div class="flex items-center justify-between">
                                    <p
                                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 m-0"
                                    >
                                        Users who shared their wallets with you.
                                        You can see and manage their wallets,
                                        but they can't see yours.
                                    </p>
                                    <add-wallet-user-btn></add-wallet-user-btn>
                                </div>
                            </div>

                            <div class="mb-10">
                                <div
                                    v-if="walletInvitations.length === 0"
                                    class="flex flex-col justify-center items-center gap-2 py-6 pb-8 self-stretch"
                                >
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="text-slate-500 dark:text-slate-400"
                                    >
                                        <g clip-path="url(#clip0_2331_4952)">
                                            <g
                                                clip-path="url(#clip1_2331_4952)"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M15 5C15 4.44772 15.4477 4 16 4C18.4853 4 20.5 6.01472 20.5 8.5C20.5 10.9853 18.4853 13 16 13C15.4477 13 15 12.5523 15 12C15 11.4477 15.4477 11 16 11C17.3807 11 18.5 9.88071 18.5 8.5C18.5 7.11929 17.3807 6 16 6C15.4477 6 15 5.55228 15 5Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M9 6C7.61929 6 6.5 7.11929 6.5 8.5C6.5 9.88071 7.61929 11 9 11C10.3807 11 11.5 9.88071 11.5 8.5C11.5 7.11929 10.3807 6 9 6ZM4.5 8.5C4.5 6.01472 6.51472 4 9 4C11.4853 4 13.5 6.01472 13.5 8.5C13.5 10.9853 11.4853 13 9 13C6.51472 13 4.5 10.9853 4.5 8.5Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M6 16C4.34315 16 3 17.3431 3 19V20C3 20.5523 2.55228 21 2 21C1.44772 21 1 20.5523 1 20V19C1 16.2386 3.23858 14 6 14H12C14.7614 14 17 16.2386 17 19V20C17 20.5523 16.5523 21 16 21C15.4477 21 15 20.5523 15 20V19C15 17.3431 13.6569 16 12 16H6Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M17.9023 15C17.9023 14.4477 18.3501 14 18.9023 14H19.0016C21.763 14 24.0016 16.2386 24.0016 19V20C24.0016 20.5523 23.5538 21 23.0016 21C22.4493 21 22.0016 20.5523 22.0016 20V19C22.0016 17.3431 20.6584 16 19.0016 16H18.9023C18.3501 16 17.9023 15.5523 17.9023 15Z"
                                                    fill="currentColor"
                                                />
                                            </g>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2331_4952">
                                                <rect
                                                    width="24"
                                                    height="24"
                                                    fill="white"
                                                />
                                            </clipPath>
                                            <clipPath id="clip1_2331_4952">
                                                <rect
                                                    width="24"
                                                    height="24"
                                                    fill="white"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <p
                                        class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                    >
                                        No wallets shared with you yet
                                    </p>
                                </div>

                                <div v-else class="overflow-hidden">
                                    <div
                                        v-for="(
                                            walletInvite, index
                                        ) in walletInvitations"
                                        :key="walletInvite.id"
                                        class="flex py-3 px-0 items-center gap-6 self-stretch"
                                        :class="{
                                            'border-b border-slate-200 dark:border-slate-700':
                                                index <
                                                walletInvitations.length - 1,
                                        }"
                                    >
                                        <div
                                            class="flex items-center gap-3 flex-1"
                                        >
                                            <div
                                                class="flex p-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700"
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="text-slate-600 dark:text-slate-300"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        clip-rule="evenodd"
                                                        d="M10.0026 3.33317C8.39173 3.33317 7.0859 4.63901 7.0859 6.24984C7.0859 7.86067 8.39173 9.1665 10.0026 9.1665C11.6134 9.1665 12.9192 7.86067 12.9192 6.24984C12.9192 4.63901 11.6134 3.33317 10.0026 3.33317ZM5.41923 6.24984C5.41923 3.71853 7.47126 1.6665 10.0026 1.6665C12.5339 1.6665 14.5859 3.71853 14.5859 6.24984C14.5859 8.78114 12.5339 10.8332 10.0026 10.8332C7.47126 10.8332 5.41923 8.78114 5.41923 6.24984ZM6.66923 13.3332C5.28852 13.3332 4.16923 14.4525 4.16923 15.8332V16.6665C4.16923 17.1267 3.79613 17.4998 3.3359 17.4998C2.87566 17.4998 2.50256 17.1267 2.50256 16.6665V15.8332C2.50256 13.532 4.36804 11.6665 6.66923 11.6665H13.3359C15.6371 11.6665 17.5026 13.532 17.5026 15.8332V16.6665C17.5026 17.1267 17.1295 17.4998 16.6692 17.4998C16.209 17.4998 15.8359 17.1267 15.8359 16.6665V15.8332C15.8359 14.4525 14.7166 13.3332 13.3359 13.3332H6.66923Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </div>
                                            <div
                                                class="flex flex-col items-start gap-1 flex-1"
                                            >
                                                <div
                                                    class="flex items-center gap-2"
                                                >
                                                    <span
                                                        class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                                    >
                                                        {{ walletInvite.email }}
                                                    </span>
                                                    <div
                                                        v-if="
                                                            walletInvite.status ===
                                                            'N'
                                                        "
                                                        class="flex px-2 py-0.5 justify-center items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700"
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 16 16"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="text-slate-500 dark:text-slate-400"
                                                        >
                                                            <path
                                                                fill-rule="evenodd"
                                                                clip-rule="evenodd"
                                                                d="M8.00004 2.66683C5.05452 2.66683 2.66671 5.05464 2.66671 8.00016C2.66671 10.9457 5.05452 13.3335 8.00004 13.3335C10.9456 13.3335 13.3334 10.9457 13.3334 8.00016C13.3334 5.05464 10.9456 2.66683 8.00004 2.66683ZM1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016C14.6667 11.6821 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.6821 1.33337 8.00016ZM8.00004 4.66683C8.36823 4.66683 8.66671 4.96531 8.66671 5.3335V7.72402L10.1381 9.19543C10.3985 9.45578 10.3985 9.87789 10.1381 10.1382C9.87776 10.3986 9.45565 10.3986 9.1953 10.1382L7.52864 8.47157C7.40361 8.34654 7.33337 8.17697 7.33337 8.00016V5.3335C7.33337 4.96531 7.63185 4.66683 8.00004 4.66683Z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                        <span
                                                            class="text-xs font-medium leading-4 text-slate-500 dark:text-slate-400"
                                                        >
                                                            Pending their
                                                            acceptance
                                                        </span>
                                                    </div>
                                                </div>
                                                <span
                                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                                >
                                                    Invited:
                                                    {{
                                                        formatDate(
                                                            walletInvite.created_at
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <Link
                                                v-if="
                                                    walletInvite.status === 'N'
                                                "
                                                :href="`/settings/invitations/${walletInvite.guid}/resend`"
                                                class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                            >
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g
                                                        clip-path="url(#clip0_2331_17912)"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C11.9017 17.5 13.6687 16.7913 15.0099 15.6213V16.6667C15.0099 17.1269 15.383 17.5 15.8432 17.5C16.3034 17.5 16.6765 17.1269 16.6765 16.6667V13.9583C16.6765 13.1529 16.0236 12.5 15.2182 12.5L12.5099 12.5C12.0496 12.5 11.6765 12.8731 11.6765 13.3333C11.6765 13.7936 12.0496 14.1667 12.5099 14.1667H14.131C13.0634 15.1965 11.5921 15.8333 10 15.8333C6.77834 15.8333 4.16667 13.2217 4.16667 10C4.16667 9.53976 3.79357 9.16667 3.33333 9.16667C2.8731 9.16667 2.5 9.53976 2.5 10ZM16.6667 10.8333C17.1269 10.8333 17.5 10.4602 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C8.10298 2.5 6.33993 3.2052 5 4.37007V3.33333C5 2.8731 4.6269 2.5 4.16667 2.5C3.70643 2.5 3.33333 2.8731 3.33333 3.33333L3.33333 6.04167C3.33333 6.84708 3.98625 7.5 4.79167 7.5H7.29167C7.7519 7.5 8.125 7.1269 8.125 6.66667C8.125 6.20643 7.7519 5.83333 7.29167 5.83333H5.86903C6.93664 4.80345 8.4079 4.16667 10 4.16667C13.2217 4.16667 15.8333 6.77834 15.8333 10C15.8333 10.4602 16.2064 10.8333 16.6667 10.8333Z"
                                                            fill="currentColor"
                                                        />
                                                    </g>
                                                    <defs>
                                                        <clipPath
                                                            id="clip0_2331_17912"
                                                        >
                                                            <rect
                                                                width="20"
                                                                height="20"
                                                                fill="white"
                                                            />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                                Resend
                                            </Link>

                                            <button
                                                type="button"
                                                class="flex items-center px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                                @click="
                                                    openRejectModal(
                                                        walletInvite.guid,
                                                        walletInvite.email
                                                    )
                                                "
                                            >
                                                <svg
                                                    v-if="
                                                        walletInvite.status ===
                                                        'A'
                                                    "
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        clip-rule="evenodd"
                                                        d="M6.18163 4.1665H2.5013C2.04106 4.1665 1.66797 4.5396 1.66797 4.99984C1.66797 5.46007 2.04106 5.83317 2.5013 5.83317H3.38834L4.06609 15.9995C4.15364 17.3127 5.2444 18.3332 6.56056 18.3332H13.442C14.7582 18.3332 15.849 17.3127 15.9365 15.9995L16.6143 5.83317H17.5013C17.9615 5.83317 18.3346 5.46007 18.3346 4.99984C18.3346 4.5396 17.9615 4.1665 17.5013 4.1665H13.821C13.1781 2.69523 11.7107 1.6665 10.0013 1.6665C8.29196 1.6665 6.82451 2.69523 6.18163 4.1665ZM8.13771 4.1665H11.8649C11.4069 3.65494 10.7413 3.33317 10.0013 3.33317C9.26138 3.33317 8.59575 3.65494 8.13771 4.1665ZM13.2108 5.83317H5.05871L5.72907 15.8886C5.75825 16.3264 6.12184 16.6665 6.56056 16.6665H13.442C13.8808 16.6665 14.2444 16.3264 14.2735 15.8886L14.9439 5.83317H13.2473C13.2351 5.83344 13.2229 5.83344 13.2108 5.83317ZM8.33464 8.33317C8.79487 8.33317 9.16797 8.70627 9.16797 9.1665V13.3332C9.16797 13.7934 8.79487 14.1665 8.33464 14.1665C7.8744 14.1665 7.5013 13.7934 7.5013 13.3332V9.1665C7.5013 8.70627 7.8744 8.33317 8.33464 8.33317ZM11.668 8.33317C12.1282 8.33317 12.5013 8.70627 12.5013 9.1665V13.3332C12.5013 13.7934 12.1282 14.1665 11.668 14.1665C11.2077 14.1665 10.8346 13.7934 10.8346 13.3332V9.1665C10.8346 8.70627 11.2077 8.33317 11.668 8.33317Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                                <div
                                                    v-else
                                                    class="flex items-center gap-2"
                                                >
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 20 20"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            clip-rule="evenodd"
                                                            d="M3.57733 3.57757C3.90277 3.25214 4.4304 3.25214 4.75584 3.57757L9.99992 8.82165L15.244 3.57757C15.5694 3.25214 16.0971 3.25214 16.4225 3.57757C16.7479 3.90301 16.7479 4.43065 16.4225 4.75609L11.1784 10.0002L16.4225 15.2442C16.7479 15.5697 16.7479 16.0973 16.4225 16.4228C16.0971 16.7482 15.5694 16.7482 15.244 16.4228L9.99992 11.1787L4.75584 16.4228C4.4304 16.7482 3.90277 16.7482 3.57733 16.4228C3.25189 16.0973 3.25189 15.5697 3.57733 15.2442L8.82141 10.0002L3.57733 4.75609C3.25189 4.43065 3.25189 3.90301 3.57733 3.57757Z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                    <span>
                                                        {{
                                                            walletInvite.status ===
                                                            'A'
                                                                ? 'Revoke'
                                                                : 'Revoke'
                                                        }}
                                                    </span>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <add-wallet-user-modal
            :localwallet="page.props.userData?.wallet_id"
            :refreshview="false"
        ></add-wallet-user-modal>

        <!-- Delete Confirmation Dialog -->
        <ConfirmationDialog
            :show="showDeleteConfirmation"
            title="Delete access to your wallet"
            :dialog="`You are about to delete access to your wallet for ${inviteUserName}. This action cannot be undone.`"
            icon="warning"
            iconColor="red"
            :buttons="[
                {
                    type: 'cancel',
                    text: 'Cancel',
                },
                {
                    type: 'confirm',
                    text: 'Delete access',
                    value: true,
                    classes: '!bg-red-500 !text-white',
                },
            ]"
            @cancel="cancelDeleteInvite"
            @confirm="confirmDeleteInvite"
        />

        <!-- Reject Confirmation Dialog -->
        <ConfirmationDialog
            :show="showRejectConfirmation"
            title="Reject invitation"
            :dialog="`You are about to reject ${rejectUserName}'s invitation to share your wallet with them.`"
            icon="warning"
            iconColor="red"
            :buttons="[
                {
                    type: 'cancel',
                    text: 'Cancel',
                },
                {
                    type: 'confirm',
                    text: 'Reject invitation',
                    value: true,
                    classes: '!bg-red-500 !text-white',
                },
            ]"
            @cancel="cancelRejectInvite"
            @confirm="confirmRejectInvite"
        />
    </div>
</template>

<script setup>
import { router } from '@inertiajs/vue3';
import { ref, onMounted, watch } from 'vue';
import Layout from '@/Layouts/Main.vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import SettingsNav from '@/Pages/Settings/partials/nav.vue';
import AddWalletUserBtn from '@/components/wallet/add-wallet-user-btn.vue';
import AddWalletUserModal from '@/components/wallet/modals/add-wallet-user.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { notify } from 'notiwind';
import str from '@/helpers/str';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';

const props = defineProps({
    user_invitations: {
        type: Array,
        default: () => [],
    },
    wallet_invitations: {
        type: Array,
        default: () => [],
    },
    localwallet: {
        type: Object,
        default: () => ({}),
    },
});

// Get current user data
const page = usePage();

// Wallet store
const walletStore = useWalletStore();
const { newInvitation } = storeToRefs(walletStore);
const { clearNewInvitation } = walletStore;

// Reactive lists for invitations
const userInvitations = ref([...props.user_invitations]);
const walletInvitations = ref([...props.wallet_invitations]);

// Confirmation dialog state
const showDeleteConfirmation = ref(false);
const inviteToDelete = ref(null);
const inviteUserName = ref('');

// Reject invitation dialog state
const showRejectConfirmation = ref(false);
const inviteToReject = ref(null);
const rejectUserName = ref('');

// Handle flash messages on component mount
onMounted(() => {
    if (page.props.flash?.success) {
        notify(
            {
                group: 'success',
                title: 'Success',
                text: page.props.flash.success,
            },
            4000
        );
    }

    if (page.props.flash?.error) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: page.props.flash.error,
            },
            4000
        );
    }
});

// Watch for changes in props.wallet_invitations
watch(
    () => props.wallet_invitations,
    (newInvitations) => {
        walletInvitations.value = [...newInvitations];
    },
    { deep: true }
);

// Watch for changes in props.user_invitations
watch(
    () => props.user_invitations,
    (newUserInvitations) => {
        userInvitations.value = [...newUserInvitations];
    },
    { deep: true }
);

// Watch for new invitations from the store
watch(newInvitation, (invitation) => {
    if (invitation) {
        // Add the new invitation to the wallet invitations list
        walletInvitations.value.unshift({
            guid: invitation.guid,
            email: invitation.email,
            status: invitation.status,
            created_at: invitation.created_at,
            wallet_id: invitation.wallet_id,
        });

        // Clear the invitation from the store
        clearNewInvitation();
    }
});

function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

// Helper function to get the inviter's name from user invitation
function getInviterName(userInvite) {
    if (!userInvite || !userInvite.wallet) return 'Unknown User';

    // Find the wallet owner (inviter) by comparing wallet.user_id with wallet.users[].id
    if (
        userInvite.wallet.user_id &&
        userInvite.wallet.users &&
        Array.isArray(userInvite.wallet.users)
    ) {
        const inviter = userInvite.wallet.users.find(
            (user) => user.id === userInvite.wallet.user_id
        );

        if (inviter && inviter.name) {
            return inviter.name;
        }
    }

    return 'Unknown User';
}

// Helper function to get the inviter's email from user invitation
function getInviterEmail(userInvite) {
    if (!userInvite || !userInvite.wallet) return userInvite?.email || '';

    // Find the wallet owner (inviter) by comparing wallet.user_id with wallet.users[].id
    if (
        userInvite.wallet.user_id &&
        userInvite.wallet.users &&
        Array.isArray(userInvite.wallet.users)
    ) {
        const inviter = userInvite.wallet.users.find(
            (user) => user.id === userInvite.wallet.user_id
        );

        if (inviter && inviter.email) {
            return inviter.email;
        }
    }

    return userInvite?.email || '';
}

function deleteUserInvite(guid) {
    if (confirm('Are you sure you want to delete this invitation?')) {
        router.delete(`/settings/invitations/${guid}/delete`);
    }
}

function deleteWalletInvite(guid) {
    // Find the invitation to get user details
    const invitation = walletInvitations.value.find((inv) => inv.guid === guid);
    const userName = invitation?.email || 'Unknown User';

    // Set up confirmation dialog
    inviteToDelete.value = guid;
    inviteUserName.value = userName;
    showDeleteConfirmation.value = true;
}

function confirmDeleteInvite() {
    if (inviteToDelete.value) {
        const inviteId = inviteToDelete.value;

        router.delete(`/settings/invitations/${inviteId}/delete`, {
            onSuccess: (page) => {
                // Remove the invitation from the reactive list
                const index = walletInvitations.value.findIndex(
                    (inv) => inv.guid === inviteId
                );
                if (index !== -1) {
                    walletInvitations.value.splice(index, 1);
                }

                // Show success notification
                const message =
                    page.props.success || 'Access deleted successfully.';
                notify(
                    {
                        group: 'success',
                        title: 'Success',
                        text: message,
                    },
                    4000
                );
            },
            onError: (errors) => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to delete access.',
                    },
                    4000
                );
            },
        });
    }

    // Reset dialog state
    showDeleteConfirmation.value = false;
    inviteToDelete.value = null;
    inviteUserName.value = '';
}

function cancelDeleteInvite() {
    showDeleteConfirmation.value = false;
    inviteToDelete.value = null;
    inviteUserName.value = '';
}

function openRejectModal(guid, userName) {
    inviteToReject.value = guid;
    rejectUserName.value = userName;
    showRejectConfirmation.value = true;
}

function cancelRejectInvite() {
    showRejectConfirmation.value = false;
    inviteToReject.value = null;
    rejectUserName.value = '';
}

function acceptInvitation(guid) {
    router.get(
        `/settings/invitations/${guid}/accept`,
        {},
        {
            onSuccess: (page) => {
                // Let Inertia.js handle data refresh automatically
                // This ensures proper synchronization with invitation-banner.vue
                const message =
                    page.props.success || 'Invitation accepted successfully.';
                notify(
                    {
                        group: 'success',
                        title: 'Success',
                        text: message,
                    },
                    4000
                );
            },
            onError: (errors) => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to accept invitation.',
                    },
                    4000
                );
            },
        }
    );
}

function confirmRejectInvite() {
    if (inviteToReject.value) {
        const inviteId = inviteToReject.value;

        router.delete(`/settings/invitations/${inviteId}/delete`, {
            onSuccess: (page) => {
                // Let Inertia.js handle data refresh automatically
                // This ensures proper synchronization with invitation-banner.vue
                const message =
                    page.props.success || 'Invitation rejected successfully.';
                notify(
                    {
                        group: 'success',
                        title: 'Success',
                        text: message,
                    },
                    4000
                );
            },
            onError: (errors) => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to revoke invitation.',
                    },
                    4000
                );
            },
        });
    }

    showRejectConfirmation.value = false;
    inviteToReject.value = null;
    rejectUserName.value = '';
}
</script>
